import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHomeSlice } from 'app/pages/Home/slice';
import { getUserPreferences } from 'utils/helpers';
import { selectIsAuthenticated } from 'app/pages/Layout/slice/selectors';
import { useSelector } from 'react-redux';
import { useRestoreMustWinState } from 'app/hooks/useRestoreMustWinState';
import { shouldPreferUrlOverLocalPreferences } from 'utils/sharedViewMode';

// Module-level (not component-instance) — restore the filter snapshot from
// localStorage once per browser session, not on every mount. This hook is
// called from RoadmapHeader, which remounts on every Milestones<->Roadmap
// transition (separate top-level pages), so a per-mount ref (the previous
// hasLoadedRef) re-asserted stale localStorage values right after a Saved
// View had just correctly set them, silently clearing the Saved View's
// active highlight via the divergence check in useSavedViewActions.
let hasRestoredFilterDataPreferences = false;

/**
 * Hook to load initial filter data from localStorage into Redux state
 * This ensures filter chips are visible on initial page load
 *
 * NOTE: This hook skips localStorage restoration if a deep link is detected in the URL
 */
export function useFilterDataLoader(isRoadmapRoute: boolean) {
  const { actions } = useHomeSlice();
  const dispatch = useDispatch();
  const restoreMustWinState = useRestoreMustWinState();
  const isAuthenticated = useSelector(selectIsAuthenticated);

  useEffect(() => {
    // Only run once per session when authenticated and not already loaded
    if (!isAuthenticated || hasRestoredFilterDataPreferences) {
      return;
    }

    // This restores shared Roadmap/My Stuff preferences — meaningless (and
    // risky) while mounted on Milestones, since RoadmapHeader is shared
    // between both pages. Don't consume the one-shot flag here so a later
    // Roadmap-route mount still gets to restore.
    if (!isRoadmapRoute) {
      return;
    }

    // 🔗 CRITICAL: Check if URL has deep link state
    // If it does, skip localStorage restoration - deep link takes precedence
    if (shouldPreferUrlOverLocalPreferences()) {
      console.log(
        '🔗 Shared view or deep link detected in URL - skipping localStorage restoration'
      );
      hasRestoredFilterDataPreferences = true;
      return;
    }

    hasRestoredFilterDataPreferences = true;
    const preferences = getUserPreferences();
    console.log(
      '📂 Loading filter preferences from localStorage:',
      preferences
    );

    // Restore full filter snapshot from localStorage
    dispatch(actions.setSelectedGroups(preferences.productGroups ?? []));
    dispatch(actions.setSelectedAudiences(preferences.audiences ?? []));
    dispatch(actions.setSelectedCats(preferences.categories ?? []));
    dispatch(actions.setSelectedFeatures(preferences.features ?? []));
    dispatch(actions.setSelectedProducts(preferences.products ?? []));
    dispatch(actions.setSelectedEvents(preferences.events ?? []));
    dispatch(actions.setSelectedLanesTiers(preferences.lanesTiers ?? []));
    dispatch(actions.setGenAiFilters(preferences.genAiFilters ?? []));
    dispatch(actions.setSelectedStatus(preferences.status ?? []));
    dispatch(
      actions.setRevenueImpactFilters(preferences.revenueImpactFilters ?? [])
    );
    restoreMustWinState(preferences);
  }, [isAuthenticated, isRoadmapRoute, dispatch, actions, restoreMustWinState]);
}
